// Parent brands own their presentation; drinks are variants within that brand.
export const drinkBrands = [
  {
    id: 'dr-browns',
    name: "Dr. Brown's",
    presentation: 'deli-classics',
    logo: '/images/drinks/dr-browns/dr-browns-logo.svg',
  },
  {
    id: 'faygo',
    name: 'Faygo',
    presentation: 'detroit-pop',
    logo: '/images/drinks/faygo/logo.webp',
    defaultAfter: 'dr-browns',
  },
  {
    id: 'coca-cola',
    name: 'Coca-Cola',
    logo: '/images/drinks/coca-cola-logo.png',
  },
  {
    id: 'pure-leaf',
    name: 'Pure Leaf',
    presentation: 'tea-counter',
    logo: '/images/drinks/pure-leaf/pure-leaf-logo.svg',
  },
  {
    id: 'clearly-canadian',
    name: 'Clearly Canadian',
    presentation: 'originals-postcards',
    logo: '/images/drinks/clearly-canadian/clearly-canadian-logo.webp',
  },
  {
    id: 'snapple',
    name: 'Snapple',
    presentation: 'fruit-stand',
    logo: '/images/drinks/snapple/snapple-logo.svg',
  },
  {
    id: 'culture-pop',
    name: 'Culture Pop',
    presentation: 'soda-posters',
    logo: '/images/drinks/culture-pop/culture-pop-logo.webp',
  },
  {
    id: 'nantucket-nectars',
    name: 'Nantucket Nectars',
    presentation: 'harbor-shelves',
    logo: '/images/drinks/nantucket-nectars/nantucket-nectars-logo.webp',
  },
  ...[
    ['vernors', 'Vernors', 'Detroit’s ginger soda', 'vernors'],
    ['canada-dry', 'Canada Dry', 'Ginger ale', 'canada-dry'],
    ['pepsi', 'Pepsi', 'Colas', 'pepsi'],
    ['mountain-dew', 'Mountain Dew', 'Citrus soda', 'mountain-dew'],
    ['dr-pepper', 'Dr Pepper', '23 flavors', 'dr-pepper'],
    ['aw', 'A&W', 'Root beer', 'aw'],
  ].map(([id, name, categoryLabel, logoSlug]) => ({
    id, name, categoryLabel,
    presentation: 'soda-favorites',
    logo: `/images/drinks/soda-favorites/${logoSlug}-logo.webp`,
  })),
  {
    id: 'welchs',
    name: "Welch's",
    presentation: 'juice-labels',
    logo: '/images/drinks/welchs/logo.svg',
  },
  { id: 'squirt', name: 'Squirt', presentation: 'citrus-splash' },
  ...[
    ['budweiser', 'Budweiser'],
    ['bud-light', 'Bud Light'],
    ['michelob-ultra', 'Michelob Ultra'],
  ].map(([id, name]) => ({
    id, name, presentation: 'beer-labels',
    logo: `/images/drinks/beers/${id}-logo.webp`,
  })),
];

// The owner's linked Juice and Zero Sugar selections, verified September 6, 2026.
// Keep regular teas out: they were not part of the confirmed selection.
const snappleDrinks = [
  ['apple', 'Snapple Apple', 'apple'],
  ['kiwi-strawberry', 'Kiwi Strawberry', 'kiwi'],
  ['fruit-punch', 'Fruit Punch', 'punch'],
  ['mango-madness', 'Mango Madness', 'mango'],
  ['grapeade', 'Grapeade', 'grape'],
  ['pink-lemonade', 'Pink Lemonade', 'pink'],
  ['orangeade', 'Orangeade', 'orange'],
  ['strawberry-pineapple-lemonade', 'Strawberry Pineapple Lemonade', 'pineapple'],
  ['black-cherry-lemonade', 'Black Cherry Lemonade', 'cherry'],
  ['raspberry-peach', 'Raspberry Peach', 'raspberry'],
  ['go-bananas', 'Go Bananas', 'banana'],
  ['watermelon-lemonade', 'Watermelon Lemonade', 'watermelon'],
  ['zero-sugar-peach-tea', 'Peach Tea', 'peach', true],
  ['zero-sugar-raspberry-tea', 'Raspberry Tea', 'raspberry', true],
  ['zero-sugar-lemon-tea', 'Lemon Tea', 'lemon', true],
  ['zero-sugar-half-n-half', 'Half ’n Half', 'lemon', true],
].map(([slug, shortName, flavorKey, zeroSugar = false]) => ({
  id: `snapple-${slug}`,
  brandId: 'snapple',
  name: shortName === 'Snapple Apple' ? shortName : `Snapple ${zeroSugar ? 'Zero Sugar ' : ''}${shortName}`,
  shortName,
  flavorKey,
  zeroSugar,
  snappleCollection: zeroSugar ? 'zero-sugar' : 'juice',
  image: `/images/drinks/snapple/${slug}.webp`,
  imageWidth: slug === 'fruit-punch' ? 269 : 270,
  imageHeight: slug === 'fruit-punch' ? 799 : 800,
}));

const drinksData = [
  {
    id: 'coca-cola',
    brandId: 'coca-cola',
    name: 'Coca-Cola',
    visualStyle: 'classic',
    displayWord: 'Coca\nCola',
    caption: 'Original taste. Timeless company.',
    featureHeadline: 'An original.\nJust like you.',
    featureDescription: 'A piled-high deli sandwich and an ice-cold Coca-Cola. Some things belong together.',
    badgeTop: 'Always a',
    badgeWord: 'classic.',
    logo: '/images/drinks/coca-cola-logo.png',
  },
  {
    id: 'diet-coke',
    brandId: 'coca-cola',
    name: 'Diet Coke',
    visualStyle: 'diet',
    displayWord: 'Diet\nCoke',
    caption: 'Silver can. Star treatment.',
    featureHeadline: 'Unmistakably\nDiet Coke.',
    featureDescription: 'You know your sandwich. You know your drink. Pull up a chair for your kind of classic.',
    badgeTop: 'Your kind of',
    badgeWord: 'icon.',
    logo: '/images/drinks/diet-coke-logo.png',
  },
  {
    id: 'cherry-coke',
    brandId: 'coca-cola',
    name: 'Cherry Coke',
    visualStyle: 'cherry',
    displayWord: 'Cherry',
    flavorLabel: 'Cherry',
    logo: '/images/drinks/coca-cola-logo.png',
  },
  {
    id: 'dr-browns-cream-soda', brandId: 'dr-browns',
    name: "Dr. Brown's Cream Soda", shortName: 'Cream Soda', flavorKey: 'cream',
    featureHeadline: 'Smooth operator.',
    featureDescription: 'Bring a little cream-soda charm to your deli order.',
    image: '/images/drinks/dr-browns/cream-soda.png',
    image2x: '/images/drinks/dr-browns/cream-soda-1600.png',
  },
  {
    id: 'dr-browns-black-cherry', brandId: 'dr-browns',
    name: "Dr. Brown's Black Cherry", shortName: 'Black Cherry', flavorKey: 'cherry',
    featureHeadline: 'The bold one.',
    featureDescription: 'A little black-cherry personality for a big deli lunch.',
    image: '/images/drinks/dr-browns/black-cherry.png',
    image2x: '/images/drinks/dr-browns/black-cherry-1600.png',
  },
  {
    id: 'dr-browns-cel-ray', brandId: 'dr-browns',
    name: "Dr. Brown's Cel-Ray", shortName: 'Cel-Ray', flavorKey: 'cel-ray',
    featureHeadline: 'A deli original.',
    featureDescription: 'For the Cel-Ray crowd. There’s nothing quite like your usual.',
    image: '/images/drinks/dr-browns/cel-ray.png',
    image2x: '/images/drinks/dr-browns/cel-ray-1600.png',
  },
  {
    id: 'dr-browns-root-beer', brandId: 'dr-browns',
    name: "Dr. Brown's Root Beer", shortName: 'Root Beer', flavorKey: 'root-beer',
    featureHeadline: 'Old-school soul.',
    featureDescription: 'A familiar favorite with a front-row seat at the deli counter.',
    image: '/images/drinks/dr-browns/root-beer.png',
    image2x: '/images/drinks/dr-browns/root-beer-1600.png',
  },
  {
    id: 'dr-browns-ginger-ale', brandId: 'dr-browns',
    name: "Dr. Brown's Ginger Ale", shortName: 'Ginger Ale', flavorKey: 'ginger',
    featureHeadline: 'A little spark.',
    featureDescription: 'Ginger-ale personality, ready to keep your sandwich company.',
    image: '/images/drinks/dr-browns/ginger-ale.png',
    image2x: '/images/drinks/dr-browns/ginger-ale-1600.png',
  },
  {
    id: 'dr-browns-zero-sugar-cream-soda', brandId: 'dr-browns',
    name: "Dr. Brown's Zero Sugar Cream Soda", shortName: 'Cream Soda', flavorKey: 'cream',
    zeroSugar: true, packageName: 'Diet Cream Soda',
    featureHeadline: 'Cream-soda charm.\nZero sugar.',
    featureDescription: 'The zero sugar choice for cream-soda people.',
    image: '/images/drinks/dr-browns/diet-cream-soda.png',
    image2x: '/images/drinks/dr-browns/diet-cream-soda-1600.png',
  },
  {
    id: 'dr-browns-zero-sugar-black-cherry', brandId: 'dr-browns',
    name: "Dr. Brown's Zero Sugar Black Cherry", shortName: 'Black Cherry', flavorKey: 'cherry',
    zeroSugar: true, packageName: 'Diet Black Cherry',
    featureHeadline: 'Bold choice.\nZero sugar.',
    featureDescription: 'Black-cherry character in the zero sugar option.',
    image: '/images/drinks/dr-browns/diet-black-cherry.png',
    image2x: '/images/drinks/dr-browns/diet-black-cherry-1600.png',
  },
  {
    id: 'pure-leaf-unsweetened-black-tea', brandId: 'pure-leaf',
    name: 'Pure Leaf Unsweetened Black Tea', shortName: 'Unsweetened',
    teaCollection: 'classics', flavorKey: 'unsweetened',
    featureHeadline: 'Simply your cup of tea.',
    image: '/images/drinks/pure-leaf/unsweetened-black-tea-bottle.png',
    imageWidth: 983, imageHeight: 1754, imageLayout: 'with-shadow',
  },
  {
    id: 'pure-leaf-sweet-black-tea', brandId: 'pure-leaf',
    name: 'Pure Leaf Sweet Black Tea', shortName: 'Sweet',
    teaCollection: 'classics', flavorKey: 'sweet',
    featureHeadline: 'Sweet on lunch.',
    image: '/images/drinks/pure-leaf/sweet-black-tea-bottle.png',
    imageWidth: 324, imageHeight: 1050,
  },
  {
    id: 'pure-leaf-extra-sweet-black-tea', brandId: 'pure-leaf',
    name: 'Pure Leaf Extra Sweet Black Tea', shortName: 'Extra Sweet',
    teaCollection: 'classics', flavorKey: 'extra-sweet',
    featureHeadline: 'Make it extra.',
    image: '/images/drinks/pure-leaf/extra-sweet-black-tea-bottle.png',
    imageWidth: 324, imageHeight: 1050,
  },
  {
    id: 'pure-leaf-raspberry-black-tea', brandId: 'pure-leaf',
    name: 'Pure Leaf Raspberry Black Tea', shortName: 'Raspberry',
    teaCollection: 'fruit', flavorKey: 'raspberry',
    featureHeadline: 'Berry good company.',
    image: '/images/drinks/pure-leaf/raspberry-black-tea-bottle.png',
    imageWidth: 324, imageHeight: 1050,
  },
  {
    id: 'pure-leaf-lemon-black-tea', brandId: 'pure-leaf',
    name: 'Pure Leaf Lemon Black Tea', shortName: 'Lemon',
    teaCollection: 'fruit', flavorKey: 'lemon',
    featureHeadline: 'A sunny little twist.',
    image: '/images/drinks/pure-leaf/lemon-black-tea-bottle.png',
    imageWidth: 324, imageHeight: 1050,
  },
  {
    id: 'pure-leaf-blackberry-black-tea', brandId: 'pure-leaf',
    name: 'Pure Leaf Blackberry Black Tea', shortName: 'Blackberry',
    teaCollection: 'fruit', flavorKey: 'blackberry',
    featureHeadline: 'Your bold berry pick.',
    image: '/images/drinks/pure-leaf/blackberry-black-tea-bottle.png',
    imageWidth: 324, imageHeight: 1050,
  },
  {
    id: 'clearly-canadian-mountain-blackberry', brandId: 'clearly-canadian',
    name: 'Clearly Canadian Mountain Blackberry', shortName: 'Blackberry',
    flavorPrefix: 'Mountain', flavorKey: 'blackberry',
    image: '/images/drinks/clearly-canadian/mountain-blackberry.webp',
  },
  {
    id: 'clearly-canadian-country-raspberry', brandId: 'clearly-canadian',
    name: 'Clearly Canadian Country Raspberry', shortName: 'Raspberry',
    flavorPrefix: 'Country', flavorKey: 'raspberry',
    image: '/images/drinks/clearly-canadian/country-raspberry.webp',
  },
  {
    id: 'clearly-canadian-orchard-peach', brandId: 'clearly-canadian',
    name: 'Clearly Canadian Orchard Peach', shortName: 'Peach',
    flavorPrefix: 'Orchard', flavorKey: 'peach',
    image: '/images/drinks/clearly-canadian/orchard-peach.webp',
  },
  {
    id: 'clearly-canadian-summer-strawberry', brandId: 'clearly-canadian',
    name: 'Clearly Canadian Summer Strawberry', shortName: 'Strawberry',
    flavorPrefix: 'Summer', flavorKey: 'strawberry',
    image: '/images/drinks/clearly-canadian/summer-strawberry.webp',
  },
  {
    id: 'clearly-canadian-wild-cherry', brandId: 'clearly-canadian',
    name: 'Clearly Canadian Wild Cherry', shortName: 'Cherry',
    flavorPrefix: 'Wild', flavorKey: 'cherry',
    image: '/images/drinks/clearly-canadian/wild-cherry.webp',
  },
  ...snappleDrinks,
  // The four Culture Pop flavors confirmed by the owner's product photo.
  ...[
    ['watermelon-lime', 'Watermelon', '& lime', 'watermelon'],
    ['wild-berries-lime', 'Wild Berries', '& lime', 'berries'],
    ['strawberry-rhubarb', 'Strawberry', '& rhubarb', 'strawberry'],
    ['black-cherry', 'Black Cherry', '', 'cherry'],
  ].map(([slug, shortName, flavorNote, flavorKey]) => ({
    id: `culture-pop-${slug}`,
    brandId: 'culture-pop',
    name: `Culture Pop ${shortName}${flavorNote ? ` ${flavorNote}` : ''}`,
    shortName,
    flavorNote,
    flavorKey,
    image: `/images/drinks/culture-pop/${slug}.webp`,
    imageWidth: slug === 'black-cherry' ? 600 : 250,
    imageHeight: slug === 'black-cherry' ? 600 : 500,
  })),
  // Owner-confirmed plastic bottles. Preserve legacy IDs for saved availability
  // and featured items; apple/orange display names follow the current labels.
  ...[
    ['pressed-apple', 'Orchard Apple', 'apple', 425],
    ['premium-orange', 'Island Orange', 'orange', 700],
    ['squeezed-lemonade', 'Squeezed Lemonade', 'lemon', 512],
    ['big-cranberry', 'Big Cranberry', 'cranberry', 1000],
    ['grapeade', 'Grapeade', 'grape', 512],
    ['red-plum', 'Red Plum', 'plum', 600],
    ['orange-mango', 'Orange Mango', 'mango', 600],
    ['peach-orange', 'Peach Orange', 'peach', 425],
    ['pineapple-orange-banana', 'Pineapple Orange Banana', 'banana', 425],
    ['pineapple-orange-guava', 'Pineapple Orange Guava', 'guava', 425],
    ['pomegranate-cherry', 'Pomegranate Cherry', 'cherry', 512],
    ['pomegranate-pear', 'Pomegranate Pear', 'pear', 600],
  ].map(([slug, shortName, flavorKey]) => ({
    id: `nantucket-nectars-${slug}`,
    brandId: 'nantucket-nectars',
    name: `Nantucket Nectars ${shortName}`,
    shortName,
    flavorKey,
    packaging: 'plastic-bottle',
    image: `/images/drinks/nantucket-nectars/${slug}-plastic.${slug === 'orange-mango' ? 'jpg' : 'png'}`,
    imageWidth: slug === 'orange-mango' ? 600 : 500,
    imageHeight: 600,
  })),
  // The owner's twelve confirmed sodas. Diet and Zero Sugar are separate
  // product names, not interchangeable labels. No unconfirmed variants/prices.
  ...[
    ['vernors', 'vernors', 'Vernors', 'original', 600, 600],
    ['diet-vernors', 'vernors', 'Diet Vernors', 'diet', 600, 600],
    ['canada-dry', 'canada-dry', 'Canada Dry Ginger Ale', 'original', 348, 848],
    ['diet-canada-dry', 'canada-dry', 'Diet Canada Dry Ginger Ale', 'diet', 418, 554],
    ['pepsi', 'pepsi', 'Pepsi', 'original', 600, 600],
    ['diet-pepsi', 'pepsi', 'Diet Pepsi', 'diet', 727, 727],
    ['mountain-dew', 'mountain-dew', 'Mountain Dew', 'original', 1080, 1080],
    ['diet-mountain-dew', 'mountain-dew', 'Diet Mountain Dew', 'diet', 727, 727],
    ['dr-pepper', 'dr-pepper', 'Dr Pepper', 'original', 600, 600],
    ['dr-pepper-zero-sugar', 'dr-pepper', 'Dr Pepper Zero Sugar', 'zero-sugar', 600, 600],
    ['aw-root-beer', 'aw', 'A&W Root Beer', 'original', 600, 600],
    ['aw-zero-sugar-root-beer', 'aw', 'A&W Zero Sugar Root Beer', 'zero-sugar', 600, 600],
  ].map(([id, brandId, name, variant, imageWidth, imageHeight]) => ({
    id, brandId, name, variant, imageWidth, imageHeight,
    shortName: variant === 'original' ? 'Original' : variant === 'diet' ? 'Diet' : 'Zero Sugar',
    diet: variant === 'diet',
    zeroSugar: variant === 'zero-sugar',
    image: `/images/drinks/soda-favorites/${id}.webp`,
  })),
  // Classic 16 oz glass range, not the current PET or large sparkling bottles.
  ...[
    ['concord-grape', 'Concord Grape', 'grape', 932],
    ['apple', 'Apple', 'apple', 1864],
    ['orange', 'Orange', 'orange', 512],
    ['cranberry', 'Cranberry', 'cranberry', 600],
    ['mango-passion-fruit', 'Mango Passion Fruit', 'mango', 600],
    ['tropical-carrot', 'Tropical Carrot', 'carrot', 600],
    ['fruit-punch', 'Fruit Punch', 'punch', 600],
    ['strawberry-kiwi', 'Strawberry Kiwi', 'kiwi', 600],
    ['orange-pineapple', 'Orange Pineapple', 'pineapple', 1398],
  ].map(([slug, shortName, flavorKey, imageSize]) => ({
    id: `welchs-${slug}`,
    brandId: 'welchs',
    name: `Welch's ${shortName}`,
    shortName, flavorKey,
    image: `/images/drinks/welchs/${slug}.webp`,
    imageWidth: imageSize,
    imageHeight: imageSize,
  })),
  ...[
    ['budweiser', 'Budweiser', 1200, 1200],
    ['bud-light', 'Bud Light', 545, 616],
    ['michelob-ultra', 'Michelob Ultra', 2048, 2048],
  ].map(([id, name, imageWidth, imageHeight]) => ({
    id, brandId: id, name, shortName: name, alcoholic: true, packaging: 'can',
    image: `/images/drinks/beers/${id}.webp`, imageWidth, imageHeight,
  })),
  // Append catalog rows to preserve any legacy indexed overrides.
  ...[
    ['cream', 'Vanilla Creme'], ['red-pop', 'Redpop'], ['rock-n-rye', 'Rock & Rye'],
    ['orange', 'Orange'], ['grape', 'Grape'], ['root-beer', 'Root Beer'],
  ].map(([flavorKey, shortName]) => ({
    id: `faygo-${flavorKey}`, brandId: 'faygo', name: `Faygo ${shortName}`,
    shortName, flavorKey, packaging: 'glass-bottle',
    image: `/images/drinks/faygo/${flavorKey}.webp`, imageWidth: 640, imageHeight: 853,
  })),
  { id: 'squirt', brandId: 'squirt', name: 'Squirt', shortName: 'Squirt', packaging: 'can',
    image: '/images/drinks/squirt/can.webp', imageWidth: 1200, imageHeight: 1200 },
];

export default drinksData;
